import React from 'react';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

// Real game logos served from /public/games/
// Order: Fortnite stays #1, rest is shuffled away from competitors' default order
const GAMES = [
  { id: 'fortnite',     name: 'Fortnite',          src: '/games/fortnite.png' },
  { id: 'cod',          name: 'Call of Duty',      src: '/games/cod.png' },
  { id: 'valorant',     name: 'Valorant',          src: '/games/valorant.png' },
  { id: 'minecraft',    name: 'Minecraft',         src: '/games/minecraft.png' },
  { id: 'cs2',          name: 'Counter-Strike 2',  src: '/games/cs2.png' },
  { id: 'gta5',         name: 'GTA V',             src: '/games/gta5.png' },
  { id: 'lol',          name: 'League of Legends', src: '/games/lol.png' },
  { id: 'roblox',       name: 'Roblox',            src: '/games/roblox.png' },
  { id: 'r6',           name: 'Rainbow Six Siege', src: '/games/r6.png' },
  { id: 'rocket',       name: 'Rocket League',     src: '/games/rocketleague.png' },
  { id: 'rust',         name: 'Rust',              src: '/games/rust.png' },
];

const CompatibleGamesSection = () => {
  return (
    <section className="relative py-12 md:py-24 px-4 md:px-8 overflow-hidden">
      {/* Subtle cyan accent matching brand palette */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-[radial-gradient(circle_at_center,rgba(8,145,178,0.10),transparent_60%)]" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <AnimateOnScroll animation="fade-down" className="text-center mb-8 md:mb-14">
          <span className="inline-flex items-center gap-1.5 px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-4 md:mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            Compatibilité totale
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4 px-2">
            Compatible avec <span className="text-cyan-500">tous vos jeux</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-lg max-w-2xl mx-auto px-4">
            Nos optimisations boostent vos FPS et réduisent votre latence sur <span className="text-white font-semibold">n&apos;importe quel jeu PC</span>. Voici une sélection des plus populaires :
          </p>
        </AnimateOnScroll>

        {/* Games grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
          {GAMES.map((game, i) => (
            <AnimateOnScroll
              key={game.id}
              animation="fade-up"
              delay={i * 40}
            >
              <div
                data-testid={`game-card-${game.id}`}
                className="group relative aspect-square rounded-xl md:rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 hover:border-cyan-500/60 transition-all duration-500 overflow-hidden cursor-default md:hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(6,182,212,0.18)]"
              >
                {/* Logo — contained with subtle padding so it breathes */}
                <div className="absolute inset-0 p-3 md:p-4 flex items-center justify-center">
                  <img
                    src={game.src}
                    alt={game.name}
                    loading="lazy"
                    draggable={false}
                    className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                {/* Vignette + label that slides up on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-x-0 bottom-0 p-2 md:p-2.5 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out">
                  <p className="text-white text-[11px] md:text-sm font-bold text-center leading-tight tracking-tight drop-shadow-lg">
                    {game.name}
                  </p>
                </div>
                {/* Subtle inner ring */}
                <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-xl md:rounded-2xl pointer-events-none" />
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        {/* Footer note */}
        <AnimateOnScroll animation="fade-up" delay={300}>
          <p className="text-center text-gray-500 text-xs md:text-sm mt-8 md:mt-10">
            <span className="text-cyan-400 font-semibold">+ tous les autres jeux</span> — solo, multi, compétitif, AAA, indé… nos tweaks Windows agissent au niveau système.
          </p>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default CompatibleGamesSection;
